import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { fetchTreeData } from "../../assets/data";
import TreeIcon from "../../images/In_situTreeIcon.svg"; // As per your code
import LocationMarker from "../../images/LocationMarker.svg";
import { ReactComponent as FilterIcon } from "../../images/FilterIcon.svg";
import { ReactComponent as LayerIcon } from "../../images/MapLayersIcon.svg";
import { ReactComponent as RulerIcon } from "../../images/RulerIcon.svg";
import MarkerClusterGroup from "react-leaflet-markercluster";
import FilterPanel from "../../assets/FilterPanel";
import LayerDropdown from "../../assets/LayerDropdown";
import { message } from "antd";
import "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import { debounce } from "lodash";
import { useNavigate, useLocation } from "react-router-dom";
import TreeInfoModal from "../../assets/TreeInfoModal";
import "./MapComponent.css";

const MapComponent = () => {
  const [trees, setTrees] = useState([]);
  const [filteredTrees, setFilteredTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTree, setSelectedTree] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState("Street");
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [activeButton, setActiveButton] = useState(null);

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const filterPanelRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const position = useMemo(() => [19.116084601593794, 72.86542060979293], []);
  const indiaBounds = useMemo(() => L.latLngBounds([6.5546079, 68.1113787], [35.675147, 97.395561]), []);

  // Fetch tree data
  useEffect(() => {
    const loadTrees = async () => {
      setLoading(true);
      try {
        const data = await fetchTreeData();
        console.log("Fetched tree data:", data);
        setTrees(data);
        setFilteredTrees(data);
        if (data.length > 0 && mapRef.current) {
          const bounds = L.latLngBounds(
            data.map((tree) => [tree.geometry.coordinates[0], tree.geometry.coordinates[1]])
          );
          mapRef.current.fitBounds(bounds, { padding: [50, 50] });
        }
      } catch (error) {
        console.error("Error loading trees:", error);
        message.error("Failed to load tree data");
      } finally {
        setLoading(false);
      }
    };
    loadTrees();
  }, []);

  // Handle updated tree from navigation state
  useEffect(() => {
    if (location.state?.updatedTree) {
      const updatedTree = location.state.updatedTree;
      setTrees((prevTrees) =>
        prevTrees.map((tree) =>
          tree.properties.tid === updatedTree.properties.tid ? updatedTree : tree
        )
      );
      setFilteredTrees((prevFiltered) =>
        prevFiltered.map((tree) =>
          tree.properties.tid === updatedTree.properties.tid ? updatedTree : tree
        )
      );
      setSelectedTree(updatedTree);
      setIsModalVisible(true);
    }
  }, [location.state]);

  // Get current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => setCurrentLocation({ lat: position.coords.latitude, lng: position.coords.longitude }),
        (error) => console.error("Error getting geolocation:", error)
      );
    }
  }, []);

  // Update marker for current location
  useEffect(() => {
    if (currentLocation && mapRef.current) {
      if (markerRef.current) {
        markerRef.current.setLatLng([currentLocation.lat, currentLocation.lng]);
      }
    }
  }, [currentLocation]);

  // Handle map click to remove marker
  useEffect(() => {
    if (mapRef.current) {
      const handleMapClick = () => {
        if (markerRef.current) {
          mapRef.current.removeLayer(markerRef.current);
          markerRef.current = null;
        }
      };
      mapRef.current.on("click", handleMapClick);
      return () => mapRef.current.off("click", handleMapClick);
    }
  }, []);

  const treeIconInstance = useMemo(
    () => new L.Icon({ iconUrl: TreeIcon, iconSize: [25, 41], iconAnchor: [12, 41] }),
    []
  );

  const handleMarkerClick = useCallback((tree) => {
    setSelectedTree(tree);
    setIsModalVisible(true);
  }, []);

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedTree(null);
  };

  const handleFilterToggle = useCallback(() => {
    setActiveButton((prev) => (prev === "filter" ? null : "filter"));
    setShowFilterPanel((prev) => !prev);
    setDropdownOpen(false); // Close layer dropdown if open
  }, []);

  const handleLayerToggle = useCallback(() => {
    setActiveButton((prev) => (prev === "layer" ? null : "layer"));
    setDropdownOpen((prev) => !prev);
    setShowFilterPanel(false); // Close filter panel if open
  }, []);

  const handleFlyToCurrentLocation = useCallback(() => {
    setActiveButton((prev) => (prev === "currentLoc" ? null : "currentLoc"));
    if (currentLocation && mapRef.current) {
      mapRef.current.flyTo([currentLocation.lat, currentLocation.lng], 13, { duration: 0.9 });
      const locationIcon = new L.Icon({ iconUrl: LocationMarker, iconSize: [40, 40], iconAnchor: [20, 20] });
      if (!markerRef.current) {
        markerRef.current = L.marker([currentLocation.lat, currentLocation.lng], { icon: locationIcon }).addTo(mapRef.current);
      } else {
        markerRef.current.setLatLng([currentLocation.lat, currentLocation.lng]);
      }
    }
  }, [currentLocation]);

  const handleApplyFilters = useCallback((filteredData) => {
    setFilteredTrees(filteredData);
    if (filteredData.length > 0 && mapRef.current) {
      const bounds = L.latLngBounds(
        filteredData.map((tree) => [tree.geometry.coordinates[0], tree.geometry.coordinates[1]])
      );
      mapRef.current.fitBounds(bounds, { padding: [50, 50], duration: 1.5 });
    }
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilteredTrees(trees);
    if (mapRef.current) mapRef.current.flyTo(position, 11.5, { duration: 1.2 });
  }, [trees, position]);

  const createCustomClusterIcon = useCallback((cluster) => {
    const clusterCount = cluster.getChildCount();
    return L.divIcon({
      html: `
        <div class="enhanced-cluster-icon">
          <img src="${TreeIcon}" alt="Tree" />
          <div class="cluster-count">${clusterCount}</div>
        </div>
      `,
      className: "enhanced-cluster-wrapper",
      iconSize: [50, 50],
    });
  }, []);

  const ZoomControl = () => {
    const map = useMap();
    const handleZoomIn = () => {
      map.zoomIn();
    };
    const handleZoomOut = () => {
      map.zoomOut();
    };
    return (
      <div className="zoom-control">
        <button
          onClick={handleZoomIn}
          className="zoom-button zoom-in"
          aria-label="Zoom In"
        >
          +
        </button>
        <div className="zoom-divider"></div>
        <button
          onClick={handleZoomOut}
          className="zoom-button zoom-out"
          aria-label="Zoom Out"
        >
          -
        </button>
      </div>
    );
  };

  const markers = useMemo(() => {
    const dataToRender = filteredTrees.length > 0 ? filteredTrees : trees;
    return dataToRender
      .map((tree) => {
        if (
          !tree.geometry?.coordinates ||
          !Array.isArray(tree.geometry.coordinates) ||
          tree.geometry.coordinates.length !== 2 ||
          isNaN(tree.geometry.coordinates[0]) ||
          isNaN(tree.geometry.coordinates[1])
        ) {
          console.warn("Invalid tree data:", tree);
          return null;
        }
        const [lat, lng] = tree.geometry.coordinates;
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          console.warn(`Invalid coordinates for tree ${tree.properties.tid}: [${lat}, ${lng}]`);
          return null;
        }
        return (
          <Marker
            key={tree.properties.tid}
            position={[lat, lng]}
            icon={treeIconInstance}
            eventHandlers={{ click: () => handleMarkerClick(tree) }}
          />
        );
      })
      .filter(Boolean);
  }, [filteredTrees, trees, handleMarkerClick, treeIconInstance]);

  return (
    <div style={{ position: "relative", height: "100%", width: "100%" }}>
      {loading && <div className="loading-overlay">Loading...</div>}
      <MapContainer
        center={position}
        zoom={11.5}
        style={{ width: "100%", height: "100vh" }}
        maxBounds={indiaBounds}
        maxBoundsViscosity={1.0}
        zoomControl={false}
        ref={mapRef}
        whenCreated={(map) => (mapRef.current = map)}
      >
        {selectedLayer === "Street" && (
          <TileLayer
            url="https://www.google.cn/maps/vt?lyrs=m@189&gl=cn&x={x}&y={y}&z={z}"
            maxZoom={22}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
        )}
        {selectedLayer === "Satellite" && (
          <TileLayer
            url="http://mt0.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}&s=Ga"
            maxZoom={22}
            attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
          />
        )}
        {selectedLayer === "OpenStreetMap" && (
          <TileLayer
            url="https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png"
            maxZoom={22}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
        )}

        <div className="button-container">
          <button
            onClick={handleLayerToggle}
            className={`map-control-button ${activeButton === "layer" ? "active" : ""}`}
            aria-label="Toggle Layer Dropdown"
          >
            <LayerIcon className="button-icon" />
          </button>
          {isDropdownOpen && (
            <LayerDropdown
              onLayerChange={(layer) => {
                setSelectedLayer(layer);
                setDropdownOpen(false);
                setActiveButton(null);
              }}
              selectedLayer={selectedLayer}
              onClose={() => {
                setDropdownOpen(false);
                setActiveButton(null);
              }}
            />
          )}
          <button
            onClick={handleFlyToCurrentLocation}
            className={`map-control-button ${activeButton === "currentLoc" ? "active" : ""}`}
            aria-label="Go to Current Location"
          >
            <RulerIcon className="button-icon" />
          </button>
          {/* <button
            onClick={handleFilterToggle}
            className={`map-control-button ${activeButton === "filter" ? "active" : ""}`}
            aria-label="Toggle Filter Panel"
          >
            <FilterIcon className="button-icon" />
          </button> */}
          {/* {showFilterPanel && (
            <div ref={filterPanelRef} className="filter-panel-container">
              <FilterPanel
                data={filteredTrees.length > 0 ? filteredTrees : trees}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
              />
            </div>
          )} */}
          <ZoomControl />
        </div>

        <MarkerClusterGroup iconCreateFunction={createCustomClusterIcon} chunkedLoading>
          {markers}
        </MarkerClusterGroup>

        {selectedTree && (
          <TreeInfoModal
            key={selectedTree?.properties?.tid}
            tree={selectedTree}
            visible={isModalVisible}
            onClose={handleModalClose}
            currentLocation={currentLocation}
          />
        )}
      </MapContainer>
    </div>
  );
};

export default MapComponent;